import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  IconButton,
  TextField,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Slider,
  Stack,
  Tooltip,
  useTheme,
  useMediaQuery,
  MenuItem,
  Alert,
  Switch
} from '@mui/material';
import {
  PlayArrow as PlayArrowIcon,
  Pause as PauseIcon,
  Stop as StopIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Settings as SettingsIcon,
  YouTube as YouTubeIcon,
  LibraryMusic as LibraryMusicIcon,
  MusicNote as MusicNoteIcon,
  Help as HelpIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import PageLayout from './PageLayout';
import { useAuth } from '../contexts/AuthContext';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '../services/firebase';
import { usePlayer } from '../contexts/PlayerContext';
import { getContrastText } from '../utils/colorUtils';
import { usePlan } from '../contexts/PlanContext';
import PlanComparisonDialog from './shared/PlanComparisonDialog';

export default function Productivity({ customColors }) {
  const { currentUser } = useAuth();
  const { handlePlayVideo } = usePlayer();
  const { userPlan } = usePlan();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const timerRef = useRef(null);

  const [time, setTime] = useState(25 * 60);
  const [isRunning, setIsRunning] = useState(false);
  const [settings, setSettings] = useState({
    workDuration: 25,
    breakDuration: 5,
    longBreakDuration: 30,
    sessionsUntilLongBreak: 4,
    workSeconds: 0,
    breakSeconds: 0,
    longBreakSeconds: 0
  });
  const [openSettings, setOpenSettings] = useState(false);
  const [currentSession, setCurrentSession] = useState(1);
  const [isBreak, setIsBreak] = useState(false);
  const [videos, setVideos] = useState([]);
  const [openVideoDialog, setOpenVideoDialog] = useState(false);
  const [openEditVideoDialog, setOpenEditVideoDialog] = useState(false);
  const [editingVideo, setEditingVideo] = useState(null);
  const [videoCategories, setVideoCategories] = useState([
    'Motivacional',
    'Devocional',
    'Lo-fi',
    'Lounge',
    'Sons da Natureza',
    'Música Clássica',
    'Meditação',
    'Estudo',
    'Ambiente'
  ]);
  const [newCategory, setNewCategory] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [newVideoUrl, setNewVideoUrl] = useState('');
  const [newVideoTitle, setNewVideoTitle] = useState('');
  const [videoNote, setVideoNote] = useState('');
  const [isLoadingVideoInfo, setIsLoadingVideoInfo] = useState(false);
  const [playlists, setPlaylists] = useState([]);
  const [openPlaylistDialog, setOpenPlaylistDialog] = useState(false);
  const [newPlaylistUrl, setNewPlaylistUrl] = useState('');
  const [newPlaylistTitle, setNewPlaylistTitle] = useState('');
  const [selectedPlaylist, setSelectedPlaylist] = useState(null);
  const [playlistNote, setPlaylistNote] = useState('');
  const [deleteConfirmation, setDeleteConfirmation] = useState({ open: false, videoId: null });
  const [openPomodoroHelp, setOpenPomodoroHelp] = useState(false);
  const [openVideoHelp, setOpenVideoHelp] = useState(false);
  const [newVideoCategory, setNewVideoCategory] = useState('');
  const [showPlanDialog, setShowPlanDialog] = useState(false);
  const [sessionHistory, setSessionHistory] = useState([]);
  const [nextTimer, setNextTimer] = useState(null);
  const [isCompletingTimer, setIsCompletingTimer] = useState(false);

  // Limitações do plano básico
  const BASIC_PLAN_LIMITS = {
    maxVideos: 2,
    maxPlaylists: 1,
    pomodoroCustomization: false
  };

  // Função para verificar se atingiu o limite
  const hasReachedLimit = (type) => {
    if (userPlan === 'PRO') return false;
    
    switch (type) {
      case 'videos':
        return videos.length >= BASIC_PLAN_LIMITS.maxVideos;
      case 'playlists':
        return playlists.length >= BASIC_PLAN_LIMITS.maxPlaylists;
      default:
        return false;
    }
  };

  // Carrega as configurações salvas ao iniciar
  useEffect(() => {
    try {
      const savedSettings = localStorage.getItem('pomodoroSettings');
      if (savedSettings) {
        const parsedSettings = JSON.parse(savedSettings);
        setSettings(prev => ({
          ...prev,
          ...parsedSettings
        }));
        
        // Configura o timer inicial com as configurações salvas
        const initialMinutes = parsedSettings.workDuration;
        const initialSeconds = parsedSettings.workSeconds;
        setTime((initialMinutes * 60) + initialSeconds);
      }
    } catch (error) {
      console.error('Erro ao carregar configurações do Pomodoro:', error);
    }
  }, []);

  // Salva as configurações quando elas mudam
  useEffect(() => {
    try {
      localStorage.setItem('pomodoroSettings', JSON.stringify(settings));
    } catch (error) {
      console.error('Erro ao salvar configurações do Pomodoro:', error);
    }
  }, [settings]);

  useEffect(() => {
    loadUserData();
    // Request notification permission
    if ("Notification" in window) {
      Notification.requestPermission();
    }

    // Carregar a API do YouTube
    const tag = document.createElement('script');
    tag.src = 'https://www.youtube.com/iframe_api';
    const firstScriptTag = document.getElementsByTagName('script')[0];
    firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);

    // Adicionar event listener para visibilidade da página
    const handleVisibilityChange = () => {
      if (!currentUser) {
        // Remover estados do player que agora estão no contexto
        setTime(0);
        setIsRunning(false);
        setIsBreak(false);
        setCurrentSession(1);
        setVideos([]);
        setOpenVideoDialog(false);
        setNewVideoUrl('');
        setNewVideoTitle('');
        setVideoNote('');
        setIsLoadingVideoInfo(false);
        setPlaylists([]);
        setOpenPlaylistDialog(false);
        setNewPlaylistUrl('');
        setNewPlaylistTitle('');
        setSelectedPlaylist(null);
        setPlaylistNote('');
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Cleanup function
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [currentUser]);

  // Adicionar efeito para persistir o player entre mudanças de rota
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (!currentUser || !selectedPlaylist) {
        // Remover estados do player que agora estão no contexto
        setTime(0);
        setIsRunning(false);
        setIsBreak(false);
        setCurrentSession(1);
        setVideos([]);
        setOpenVideoDialog(false);
        setNewVideoUrl('');
        setNewVideoTitle('');
        setVideoNote('');
        setIsLoadingVideoInfo(false);
        setPlaylists([]);
        setOpenPlaylistDialog(false);
        setNewPlaylistUrl('');
        setNewPlaylistTitle('');
        setSelectedPlaylist(null);
        setPlaylistNote('');
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [currentUser, selectedPlaylist]);

  // Efeito para atualizar o timer quando nextTimer mudar
  useEffect(() => {
    if (nextTimer !== null) {
      setTime(nextTimer);
      setNextTimer(null);
    }
  }, [nextTimer]);

  const loadUserData = async () => {
    if (!currentUser) return;
    try {
      const userRef = doc(db, 'users', currentUser.uid);
      const userDoc = await getDoc(userRef);
      if (userDoc.exists()) {
        const userData = userDoc.data();
        if (userData.pomodoroSettings) {
          setSettings(userData.pomodoroSettings);
        }
        if (userData.youtubeVideos) {
          setVideos(userData.youtubeVideos);
        }
        if (userData.spotifyPlaylists) {
          setPlaylists(userData.spotifyPlaylists);
        }
      }
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    }
  };

  const saveUserData = async (newSettings, newVideos, newPlaylists) => {
    if (!currentUser) return;
    try {
      const userRef = doc(db, 'users', currentUser.uid);
      await updateDoc(userRef, {
        pomodoroSettings: newSettings || settings,
        youtubeVideos: newVideos || videos,
        spotifyPlaylists: newPlaylists || playlists
      });
    } catch (error) {
      console.error('Erro ao salvar dados:', error);
    }
  };

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const formatDuration = (minutes, seconds) => {
    if (minutes === 0) {
      return `${seconds}s`;
    } else if (seconds === 0) {
      return `${minutes}min`;
    }
    return `${minutes}min ${seconds}s`;
  };

  const handleTimerComplete = () => {
    try {
      // Se já está completando, ignora completamente
      if (isCompletingTimer) {
        console.log('Timer já está completando, ignorando chamada duplicada');
        return;
      }

      // Marca que está iniciando o processo de completar
      setIsCompletingTimer(true);
      console.log('Iniciando completion do timer');

      // Para o timer atual
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      setIsRunning(false);

      // Captura o estado atual antes de qualquer mudança
      const currentPeriod = {
        session: currentSession,
        isBreak: isBreak,
        type: isBreak 
          ? (currentSession % settings.sessionsUntilLongBreak === 0 ? 'pausa longa' : 'pausa curta')
          : 'foco',
        duration: isBreak 
          ? (currentSession % settings.sessionsUntilLongBreak === 0 
              ? settings.longBreakDuration 
              : settings.breakDuration)
          : settings.workDuration,
        seconds: isBreak 
          ? (currentSession % settings.sessionsUntilLongBreak === 0 
              ? settings.longBreakSeconds 
              : settings.breakSeconds)
          : settings.workSeconds,
        timestamp: Date.now()
      };

      // Registra o período atual e remove duplicatas depois
      setSessionHistory(prev => {
        try {
          // Adiciona o novo período
          const newHistory = [...prev, currentPeriod];
          
          // Remove duplicatas mantendo apenas a primeira ocorrência de cada período
          const uniqueHistory = newHistory.reduce((acc, current) => {
            const isDuplicate = acc.some(item => 
              item.type === current.type && 
              item.session === current.session &&
              item.isBreak === current.isBreak
            );
            if (!isDuplicate) {
              acc.push(current);
            }
            return acc;
          }, []);

          return uniqueHistory;
        } catch (error) {
          console.error('Erro ao atualizar histórico:', error);
          return prev;
        }
      });

      // Calcula e atualiza o próximo estado
      const nextIsBreak = !currentPeriod.isBreak;
      const nextSession = currentPeriod.isBreak 
        ? (currentSession + 1) // Sempre incrementa a sessão após uma pausa
        : currentSession;

      const nextMinutes = nextIsBreak
        ? (nextSession % settings.sessionsUntilLongBreak === 0 
            ? settings.longBreakDuration
            : settings.breakDuration)
        : settings.workDuration;

      const nextSeconds = nextIsBreak
        ? (nextSession % settings.sessionsUntilLongBreak === 0 
            ? settings.longBreakSeconds
            : settings.breakSeconds)
        : settings.workSeconds;

      // Atualiza os estados para o próximo período
      setIsBreak(nextIsBreak);
      setCurrentSession(nextSession);
      setTime((nextMinutes * 60) + nextSeconds);

      // Notificações
      if (Notification.permission === "granted") {
        try {
          new Notification("Pomodoro Timer", {
            body: currentPeriod.isBreak ? "Intervalo terminado! Hora de voltar ao trabalho!" : "Tempo terminado! Hora do intervalo!",
            icon: "/favicon.ico"
          });
        } catch (error) {
          console.error('Erro ao mostrar notificação:', error);
        }
      }

      // Som
      try {
        const audio = new Audio('/sounds/notification.mp3');
        audio.play().catch(error => {
          console.log('Erro ao reproduzir o áudio:', error);
        });
      } catch (error) {
        console.log('Erro ao criar o objeto de áudio:', error);
      }

      // Reseta a flag de completion
      setIsCompletingTimer(false);
    } catch (error) {
      console.error('Erro ao completar timer:', error);
      // Em caso de erro, garante que os estados são resetados
      setIsCompletingTimer(false);
      setIsRunning(false);
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
  };

  const handleStart = () => {
    try {
      // Limpa qualquer timer existente
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      
      // Se o timer estiver em 0, configura o tempo inicial
      if (time <= 0) {
        const initialMinutes = isBreak 
          ? (currentSession % settings.sessionsUntilLongBreak === 0
              ? settings.longBreakDuration
              : settings.breakDuration)
          : settings.workDuration;
        
        const initialSeconds = isBreak 
          ? (currentSession % settings.sessionsUntilLongBreak === 0
              ? settings.longBreakSeconds
              : settings.breakSeconds)
          : settings.workSeconds;

        setTime((initialMinutes * 60) + initialSeconds);
      }
      
      // Atualiza o estado de execução
      setIsRunning(true);

      // Cria um novo intervalo
      timerRef.current = setInterval(() => {
        setTime(prevTime => {
          try {
            if (prevTime <= 1) {
              // Limpa o intervalo antes de chamar handleTimerComplete
              if (timerRef.current) {
                clearInterval(timerRef.current);
                timerRef.current = null;
              }
              // Chama handleTimerComplete de forma segura
              setTimeout(() => handleTimerComplete(), 0);
              return 0;
            }
            return prevTime - 1;
          } catch (error) {
            console.error('Erro ao atualizar timer:', error);
            // Em caso de erro, para o timer e retorna o valor atual
            if (timerRef.current) {
              clearInterval(timerRef.current);
              timerRef.current = null;
            }
            setIsRunning(false);
            return prevTime;
          }
        });
      }, 1000);
    } catch (error) {
      console.error('Erro ao iniciar timer:', error);
      // Em caso de erro, garante que o timer está parado
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      setIsRunning(false);
    }
  };

  const handlePause = () => {
    setIsRunning(false);
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const handleStop = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    setIsRunning(false);
    resetTimer();
  };

  const resetTimer = () => {
    const initialMinutes = settings.workDuration;
    const initialSeconds = settings.workSeconds;
    setTime((initialMinutes * 60) + initialSeconds);
    setIsBreak(false);
    setCurrentSession(1);
    setSessionHistory([]);
  };

  const handleSettingsSave = () => {
    try {
      // Salva as configurações
      localStorage.setItem('pomodoroSettings', JSON.stringify(settings));
      
      // Atualiza o timer com as novas configurações
      const initialMinutes = settings.workDuration;
      const initialSeconds = settings.workSeconds;
      setTime((initialMinutes * 60) + initialSeconds);
      
      // Reseta o estado do timer
      setIsBreak(false);
      setCurrentSession(1);
      setSessionHistory([]);
      
      setOpenSettings(false);
    } catch (error) {
      console.error('Erro ao salvar configurações:', error);
    }
  };

  const fetchVideoInfo = async (videoId) => {
    try {
      setIsLoadingVideoInfo(true);
      const oembedResponse = await fetch(`https://www.youtube.com/oembed?url=https://www.youtube.com/watch?v=${videoId}&format=json`);
      const oembedData = await oembedResponse.json();
      if (oembedData.title) {
        setNewVideoTitle(oembedData.title);
      }
    } catch (error) {
      console.error('Erro ao buscar informações do vídeo:', error);
    } finally {
      setIsLoadingVideoInfo(false);
    }
  };

  const handleAddVideo = () => {
    if (!newVideoUrl) return;
    
    if (hasReachedLimit('videos')) {
      setShowPlanDialog(true);
      setOpenVideoDialog(false);
      return;
    }

    // Extract video ID from URL
    const videoId = newVideoUrl.match(/(?:youtu\.be\/|youtube\.com(?:\/embed\/|\/v\/|\/watch\?v=|\/watch\?.+&v=))([\w-]{11})/);
    
    if (videoId) {
      const newVideo = {
        id: Date.now().toString(),
        title: newVideoTitle,
        videoId: videoId[1],
        url: newVideoUrl,
        note: videoNote,
        category: newVideoCategory || videoCategories[0]
      };

      const updatedVideos = [...videos, newVideo];
      setVideos(updatedVideos);
      saveUserData(null, updatedVideos, null);
      handleCloseVideoDialog();
    }
  };

  const handleCloseVideoDialog = () => {
    setNewVideoUrl('');
    setNewVideoTitle('');
    setVideoNote('');
    setNewVideoCategory('');
    setOpenVideoDialog(false);
  };

  const handleEditVideo = () => {
    if (!editingVideo) return;

    const updatedVideos = videos.map(video => 
      video.id === editingVideo.id 
        ? { ...video, note: editingVideo.note, category: editingVideo.category }
        : video
    );

    setVideos(updatedVideos);
    saveUserData(null, updatedVideos, null);
    setEditingVideo(null);
    setOpenEditVideoDialog(false);
  };

  const handleAddCategory = () => {
    if (!newCategory.trim()) return;
    setVideoCategories(prev => [...prev, newCategory.trim()]);
    setNewCategory('');
  };

  const filteredVideos = selectedCategory === 'all' 
    ? videos 
    : videos.filter(video => video.category === selectedCategory);

  const handleDeleteVideo = (videoId) => {
    const updatedVideos = videos.filter(video => video.id !== videoId);
    setVideos(updatedVideos);
    saveUserData(null, updatedVideos, null);
    setDeleteConfirmation({ open: false, videoId: null });
  };

  const openDeleteConfirmation = (videoId) => {
    setDeleteConfirmation({ open: true, videoId });
  };

  const handleAddPlaylist = () => {
    if (!newPlaylistUrl) return;
    
    if (hasReachedLimit('playlists')) {
      setShowPlanDialog(true);
      setOpenPlaylistDialog(false);
      return;
    }

    // Extract playlist ID from URL
    const playlistId = newPlaylistUrl.match(/playlist\/([a-zA-Z0-9]+)/);
    
    if (playlistId) {
      const newPlaylist = {
        id: Date.now().toString(),
        title: newPlaylistTitle || 'Playlist do Spotify',
        playlistId: playlistId[1],
        url: newPlaylistUrl,
        note: playlistNote
      };

      const updatedPlaylists = [...playlists, newPlaylist];
      setPlaylists(updatedPlaylists);
      saveUserData(null, null, updatedPlaylists);
      setOpenPlaylistDialog(false);
      setNewPlaylistUrl('');
      setNewPlaylistTitle('');
      setPlaylistNote('');
    }
  };

  const handleDeletePlaylist = (playlistId) => {
    const updatedPlaylists = playlists.filter(playlist => playlist.id !== playlistId);
    setPlaylists(updatedPlaylists);
    saveUserData(null, null, updatedPlaylists);
  };

  const handlePlayPlaylist = (playlist) => {
    setSelectedPlaylist(playlist);
  };

  const handlePlayYouTubeVideo = (video) => {
    setSelectedPlaylist(null); // Stop Spotify playlist if playing
    handlePlayVideo(video);
  };

  return (
    <>
    <PageLayout
      title="Produtividade"
      hideAddButton
      customColors={customColors}
      actionButtons={
        isMobile && (
          <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleStart}
              sx={{ flex: 1, minWidth: 0, px: 1.5, fontSize: '0.75rem', whiteSpace: 'nowrap' }}
            >
              INICIAR
            </Button>
            <Button
              variant="outlined"
              color="error"
              onClick={handleStop}
              sx={{ flex: 1, minWidth: 0, px: 1.5, fontSize: '0.75rem', whiteSpace: 'nowrap' }}
            >
              PARAR
            </Button>
          </Box>
        )
      }
    >
      <Grid container spacing={3} alignItems="flex-start">
        {/* Pomodoro Timer Section - Full width */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                mb: 2 
              }}>
                <Typography variant="h6" component="div">
                  Timer Pomodoro
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <IconButton
                    onClick={() => setOpenPomodoroHelp(true)}
                    color="primary"
                    size="small"
                  >
                    <HelpIcon />
                  </IconButton>
                  <IconButton
                    onClick={() => setOpenSettings(true)}
                    color="primary"
                    size="small"
                  >
                    <SettingsIcon />
                  </IconButton>
                </Box>
              </Box>

              {/* Configurações atuais */}
              <Box sx={{ mb: 3, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                <Typography variant="subtitle2" gutterBottom color="text.secondary">
                  Configurações Atuais:
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="body2">
                      🎯 Foco: {formatDuration(settings.workDuration, settings.workSeconds)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="body2">
                      ☕ Pausa Curta: {formatDuration(settings.breakDuration, settings.breakSeconds)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="body2">
                      🌟 Pausa Longa: {formatDuration(settings.longBreakDuration, settings.longBreakSeconds)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="body2">
                      📍 Sessões até pausa longa: {settings.sessionsUntilLongBreak}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>

              {/* Timer Display */}
              <Box sx={{ textAlign: 'center', mb: 3 }}>
                <Typography variant="h6" sx={{ 
                  color: isBreak 
                    ? (currentSession % settings.sessionsUntilLongBreak === 0 
                      ? 'warning.main' // Cor laranja para pausa longa
                      : 'success.main') // Cor verde para pausa curta
                    : (customColors?.primary || 'primary.main'), // Cor primária para foco
                  mb: 1
                }}>
                  {isBreak 
                    ? (currentSession % settings.sessionsUntilLongBreak === 0 
                      ? `Sessão ${currentSession.toString().padStart(2, '0')} - Pausa Longa`
                      : `Sessão ${currentSession.toString().padStart(2, '0')} - Pausa Curta`)
                    : `Sessão ${currentSession.toString().padStart(2, '0')} - Foco`}
                </Typography>
                <Typography variant="h4" sx={{ mb: 1 }}>
                  {formatTime(time)}
                </Typography>
                <Typography variant="body2" sx={{ 
                  color: isRunning ? 'success.main' : 'text.secondary',
                  mt: 1
                }}>
                  Status: {isRunning ? 'Em andamento' : 'Aguardando'}
                </Typography>
              </Box>

              {/* Histórico de Sessões */}
              {sessionHistory.length > 0 && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle2" gutterBottom color="text.secondary">
                    Histórico da Sessão Atual:
                  </Typography>
                  <Stack spacing={1}>
                    {sessionHistory.map((session, index) => (
                      <Box 
                        key={index}
                        sx={{ 
                          p: 1, 
                          bgcolor: 'background.default',
                          borderRadius: 1,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1
                        }}
                      >
                        <Typography variant="body2" sx={{ 
                          color: session.type === 'foco' 
                            ? (customColors?.primary || 'primary.main')
                            : (session.type === 'pausa longa' 
                              ? 'warning.main' // Cor laranja para pausa longa
                              : 'success.main') // Cor verde para pausa curta
                        }}>
                          {session.type === 'pausa longa' 
                            ? 'Pausa Longa'
                            : `Sessão ${session.session.toString().padStart(2, '0')} - ${session.type}`}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          ({formatDuration(session.duration, session.seconds)})
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* YouTube Videos Section - Left column */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              {userPlan === 'BASIC' && (
                <Alert 
                  severity="info" 
                  sx={{ mb: 2 }}
                  action={
                    <Button 
                      color="inherit" 
                      size="small" 
                      onClick={() => {/* Adicionar função para upgrade */}}
                    >
                      FAZER UPGRADE
                    </Button>
                  }
                >
                  Plano Básico: {videos.length}/{BASIC_PLAN_LIMITS.maxVideos} vídeos disponíveis
                </Alert>
              )}
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                mb: 2 
              }}>
                <Typography variant="h6" component="div">
                  Vídeos para Foco
                </Typography>
                  <IconButton
                    onClick={() => setOpenVideoHelp(true)}
                    color="primary"
                    size="small"
                  >
                    <HelpIcon />
                  </IconButton>
                </Box>
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: 'column',
                  mb: 2 
                }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    Adicione vídeos ou playlists do YouTube para manter o foco durante suas sessões de trabalho. O áudio será reproduzido em segundo plano enquanto você usa outras ferramentas.
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontStyle: 'italic' }}>
                    Dica: Você pode adicionar vídeos de sons ambientes, lo-fi, música para estudar, etc.
                  </Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setOpenVideoDialog(true)}
                  sx={{
                    bgcolor: customColors?.primary || 'primary.main',
                      color: customColors?.primary ? getContrastText(customColors.primary) : 'white',
                    '&:hover': {
                      bgcolor: customColors?.primary || 'primary.main',
                      opacity: 0.9
                      },
                      alignSelf: 'flex-start',
                      mt: 2,
                      mb: 2
                  }}
                >
                  Adicionar Vídeo
                </Button>
              </Box>

                {/* Filter Section */}
                <Box sx={{ mb: 2 }}>
                  <TextField
                    select
                    fullWidth
                    label="Filtrar por categoria"
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    size="small"
                  >
                    <MenuItem value="all">Todas as categorias</MenuItem>
                    {videoCategories.map((category) => (
                      <MenuItem key={category} value={category}>
                        {category}
                      </MenuItem>
                    ))}
                  </TextField>
                </Box>

                {/* Video List */}
              <Grid container spacing={2}>
                  {filteredVideos.map((video) => (
                  <Grid item xs={12} key={video.id}>
                      <Card 
                        variant="outlined"
                        sx={{
                          bgcolor: selectedPlaylist?.id === video.id ? 'action.selected' : 'inherit'
                        }}
                      >
                        <CardContent sx={{ 
                          display: 'flex',
                          flexDirection: { xs: 'column', sm: 'row' },
                          justifyContent: 'space-between',
                          alignItems: { xs: 'flex-start', sm: 'center' },
                          py: 1,
                          '&:last-child': { pb: 1 }
                        }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                            <YouTubeIcon color="error" />
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="body2" noWrap sx={{ maxWidth: { xs: '100%', sm: 200 } }}>
                                {video.title}
                              </Typography>
                              {video.category && (
                                <Typography 
                                  variant="caption"
                                  sx={{ 
                                    display: 'inline-block',
                                    bgcolor: 'action.selected',
                                    px: 1,
                                    py: 0.25,
                                    borderRadius: 1,
                                    mb: 0.5
                                  }}
                                >
                                  {video.category}
                                </Typography>
                              )}
                              {video.note && (
                                <Typography 
                                  variant="caption" 
                                  color="text.secondary" 
                                  sx={{ 
                                    display: 'block',
                                    fontStyle: 'italic',
                                    maxWidth: { xs: '100%', sm: 200 },
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                  }}
                                >
                                  {video.note}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                                  <Box sx={{ 
                                    display: 'flex',
                                    justifyContent: { xs: 'center', sm: 'flex-end' },
                                    width: '100%',
                                    mt: { xs: 1, sm: 0 },
                                    borderTop: { xs: `1px solid ${theme.palette.divider}`, sm: 'none' },
                                    pt: { xs: 1, sm: 0 },
                                    gap: { xs: 2, sm: 0 }
                                  }}>
                                    <IconButton
                                      size="small"
                                      onClick={() => {
                                        setEditingVideo(video);
                                        setOpenEditVideoDialog(true);
                                      }}
                                      color="primary"
                                    >
                                      <EditIcon />
                                    </IconButton>
                                    <IconButton
                                      size="small"
                                      onClick={() => selectedPlaylist?.id === video.id ? setSelectedPlaylist(null) : handlePlayYouTubeVideo(video)}
                                      sx={{ 
                                        color: selectedPlaylist?.id === video.id ? 'error.main' : (customColors?.primary || 'primary.main')
                                      }}
                                    >
                                      {selectedPlaylist?.id === video.id ? <StopIcon /> : <PlayArrowIcon />}
                                    </IconButton>
                                    <IconButton
                                      size="small"
                                      onClick={() => openDeleteConfirmation(video.id)}
                                      color="error"
                                    >
                                      <DeleteIcon />
                                    </IconButton>
                                  </Box>
                                </CardContent>
                              </Card>
                            </Grid>
                          ))}
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Spotify Playlists Section - Right column */}
                  <Grid item xs={12} md={6}>
                    <Card sx={{ height: '100%' }}>
                      <CardContent>
                        {userPlan === 'BASIC' && (
                          <Alert 
                            severity="info" 
                            sx={{ mb: 2 }}
                            action={
                              <Button 
                                color="inherit" 
                                size="small" 
                                onClick={() => {/* Adicionar função para upgrade */}}
                              >
                                FAZER UPGRADE
                              </Button>
                            }
                          >
                            Plano Básico: {playlists.length}/{BASIC_PLAN_LIMITS.maxPlaylists} playlist disponível
                          </Alert>
                        )}
                        <Box sx={{ 
                          display: 'flex', 
                          flexDirection: 'column',
                          mb: 2 
                        }}>
                          <Typography variant="h6" component="div" gutterBottom>
                            Playlists do Spotify
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            É necessário estar logado em sua conta do Spotify para reproduzir as playlists na íntegra
                          </Typography>
                          <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={() => setOpenPlaylistDialog(true)}
                            sx={{
                              bgcolor: customColors?.primary || 'primary.main',
                              color: customColors?.primary ? getContrastText(customColors.primary) : 'white',
                              '&:hover': {
                                bgcolor: customColors?.primary || 'primary.main',
                                opacity: 0.9
                              },
                              alignSelf: 'flex-start',
                              mt: 2
                            }}
                          >
                            Adicionar Playlist
                          </Button>
                        </Box>

                        {/* Player Section */}
                        {selectedPlaylist && (
                          <Box sx={{ mb: 2 }}>
                            <Card variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'background.default' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                                <LibraryMusicIcon color="success" sx={{ fontSize: 40 }} />
                                <Box sx={{ flex: 1 }}>
                                  <Typography variant="subtitle1" sx={{ mb: 0.5 }}>
                                    Reproduzindo: {selectedPlaylist.title}
                                  </Typography>
                                  {selectedPlaylist.note && (
                                    <Typography variant="caption" color="text.secondary">
                                      {selectedPlaylist.note}
                                    </Typography>
                                  )}
                                </Box>
                                <IconButton
                                  size="small"
                                  onClick={() => setSelectedPlaylist(null)}
                                  color="error"
                                >
                                  <StopIcon />
                                </IconButton>
                              </Box>
                              <Box sx={{ width: '100%', height: '80px' }}>
                                <iframe
                                  src={`https://open.spotify.com/embed/playlist/${selectedPlaylist.playlistId}`}
                                  width="100%"
                                  height="80"
                                  frameBorder="0"
                                  allow="encrypted-media"
                                  style={{ borderRadius: '8px' }}
                                />
                              </Box>
                            </Card>
                          </Box>
                        )}

                        {/* Playlist List */}
                        <Grid container spacing={2}>
                          {playlists.map((playlist) => (
                            <Grid item xs={12} key={playlist.id}>
                              <Card 
                                variant="outlined"
                                sx={{
                                  bgcolor: selectedPlaylist?.id === playlist.id ? 'action.selected' : 'inherit'
                                }}
                              >
                      <CardContent sx={{ 
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        py: 1,
                        '&:last-child': { pb: 1 }
                      }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <MusicNoteIcon color="success" />
                                    <Box>
                          <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                                        {playlist.title}
                                      </Typography>
                                      {playlist.note && (
                                        <Typography 
                                          variant="caption" 
                                          color="text.secondary" 
                                          sx={{ 
                                            display: 'block',
                                            fontStyle: 'italic',
                                            maxWidth: 200,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap'
                                          }}
                                        >
                                          {playlist.note}
                          </Typography>
                                      )}
                                    </Box>
                        </Box>
                        <Box>
                          <IconButton
                            size="small"
                                      onClick={() => selectedPlaylist?.id === playlist.id ? setSelectedPlaylist(null) : handlePlayPlaylist(playlist)}
                                      sx={{ 
                                        color: selectedPlaylist?.id === playlist.id ? 'error.main' : (customColors?.primary || 'primary.main')
                                      }}
                                    >
                                      {selectedPlaylist?.id === playlist.id ? <StopIcon /> : <PlayArrowIcon />}
                          </IconButton>
                          <IconButton
                            size="small"
                                      onClick={() => handleDeletePlaylist(playlist.id)}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

                {/* Delete Video Confirmation Dialog */}
                <Dialog
                  open={deleteConfirmation.open}
                  onClose={() => setDeleteConfirmation({ open: false, videoId: null })}
                >
                  <DialogTitle>Confirmar Exclusão</DialogTitle>
                  <DialogContent>
                    <Typography>
                      Tem certeza que deseja excluir este vídeo da sua lista?
                    </Typography>
                  </DialogContent>
                  <DialogActions>
                    <Button 
                      onClick={() => setDeleteConfirmation({ open: false, videoId: null })}
                    >
                      Cancelar
                    </Button>
                    <Button 
                      onClick={() => handleDeleteVideo(deleteConfirmation.videoId)}
                      color="error"
                      variant="contained"
                    >
                      Excluir
                    </Button>
                  </DialogActions>
                </Dialog>

      {/* Settings Dialog */}
      <Dialog 
        open={openSettings} 
        onClose={() => setOpenSettings(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Configurações do Pomodoro</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 2 }}>
            {/* Tempo de Trabalho */}
            <Box>
              <Typography gutterBottom>
                Tempo de Trabalho: {settings.workDuration}min {settings.workSeconds}s
              </Typography>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">Minutos</Typography>
                  <Slider
                    value={settings.workDuration}
                    onChange={(e, value) => setSettings(prev => ({ ...prev, workDuration: value }))}
                    min={0}
                    max={60}
                    marks={[
                      { value: 25, label: '25' },
                      { value: 45, label: '45' }
                    ]}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">Segundos</Typography>
                  <Slider
                    value={settings.workSeconds}
                    onChange={(e, value) => setSettings(prev => ({ ...prev, workSeconds: value }))}
                    min={0}
                    max={59}
                    marks={[
                      { value: 0, label: '0' },
                      { value: 30, label: '30' }
                    ]}
                  />
                </Grid>
              </Grid>
            </Box>

            {/* Tempo de Pausa */}
            <Box>
              <Typography gutterBottom>
                Tempo de Pausa: {settings.breakDuration}min {settings.breakSeconds}s
              </Typography>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">Minutos</Typography>
                  <Slider
                    value={settings.breakDuration}
                    onChange={(e, value) => setSettings(prev => ({ ...prev, breakDuration: value }))}
                    min={0}
                    max={30}
                    marks={[
                      { value: 5, label: '5' },
                      { value: 15, label: '15' }
                    ]}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">Segundos</Typography>
                  <Slider
                    value={settings.breakSeconds}
                    onChange={(e, value) => setSettings(prev => ({ ...prev, breakSeconds: value }))}
                    min={0}
                    max={59}
                    marks={[
                      { value: 0, label: '0' },
                      { value: 30, label: '30' }
                    ]}
                  />
                </Grid>
              </Grid>
            </Box>

            {/* Tempo de Pausa Longa */}
            <Box>
              <Typography gutterBottom>
                Tempo de Pausa Longa: {settings.longBreakDuration}min {settings.longBreakSeconds}s
              </Typography>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">Minutos</Typography>
                  <Slider
                    value={settings.longBreakDuration}
                    onChange={(e, value) => setSettings(prev => ({ ...prev, longBreakDuration: value }))}
                    min={0}
                    max={60}
                    marks={[
                      { value: 15, label: '15' },
                      { value: 30, label: '30' }
                    ]}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">Segundos</Typography>
                  <Slider
                    value={settings.longBreakSeconds}
                    onChange={(e, value) => setSettings(prev => ({ ...prev, longBreakSeconds: value }))}
                    min={0}
                    max={59}
                    marks={[
                      { value: 0, label: '0' },
                      { value: 30, label: '30' }
                    ]}
                  />
                </Grid>
              </Grid>
            </Box>

            {/* Sessões até Pausa Longa */}
            <Box>
              <Typography gutterBottom>
                Sessões até Pausa Longa: {settings.sessionsUntilLongBreak}
              </Typography>
              <Slider
                value={settings.sessionsUntilLongBreak}
                onChange={(e, value) => setSettings(prev => ({ ...prev, sessionsUntilLongBreak: value }))}
                min={1}
                max={8}
                marks={[
                  { value: 4, label: '4' },
                  { value: 6, label: '6' }
                ]}
              />
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenSettings(false)}>Cancelar</Button>
          <Button 
            onClick={handleSettingsSave} 
            variant="contained"
            sx={{
              bgcolor: customColors?.primary || 'primary.main',
              color: customColors?.primary ? getContrastText(customColors.primary) : 'white',
              '&:hover': {
                bgcolor: customColors?.primary || 'primary.main',
                opacity: 0.9
              }
            }}
          >
            Salvar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Video Dialog */}
                <Dialog open={openVideoDialog} onClose={handleCloseVideoDialog}>
        <DialogTitle>Adicionar Vídeo do YouTube</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 2, minWidth: 300 }}>
            <TextField
              label="URL do YouTube"
              value={newVideoUrl}
                        onChange={(e) => {
                          setNewVideoUrl(e.target.value);
                          const videoId = e.target.value.match(/(?:youtu\.be\/|youtube\.com(?:\/embed\/|\/v\/|\/watch\?v=|\/watch\?.+&v=))([\w-]{11})/);
                          if (videoId) {
                            fetchVideoInfo(videoId[1]);
                          }
                        }}
              fullWidth
              helperText="Ex: https://youtube.com/watch?v=..."
            />
                      <TextField
                        label="Título do Vídeo"
                        value={newVideoTitle}
                        disabled
                        fullWidth
                        InputProps={{
                          readOnly: true,
                        }}
                        helperText="O título será obtido automaticamente do YouTube"
                      />
                      <TextField
                        select
                        label="Categoria"
                        value={newVideoCategory || videoCategories[0]}
                        onChange={(e) => setNewVideoCategory(e.target.value)}
                        fullWidth
                      >
                        {videoCategories.map((category) => (
                          <MenuItem key={category} value={category}>
                            {category}
                          </MenuItem>
                        ))}
                      </TextField>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <TextField
                          label="Nova categoria"
                          value={newCategory}
                          onChange={(e) => setNewCategory(e.target.value)}
                          size="small"
                          fullWidth
                        />
                        <Button
                          variant="outlined"
                          onClick={handleAddCategory}
                          sx={{ whiteSpace: 'nowrap' }}
                        >
                          Adicionar
                        </Button>
                      </Box>
                      <TextField
                        label="Anotações (opcional)"
                        value={videoNote}
                        onChange={(e) => setVideoNote(e.target.value)}
                        fullWidth
                        multiline
                        rows={3}
                        placeholder="Adicione suas anotações sobre este vídeo..."
                      />
                    </Stack>
                  </DialogContent>
                  <DialogActions>
                    <Button onClick={handleCloseVideoDialog}>Cancelar</Button>
                    <Button 
                      onClick={handleAddVideo}
                      variant="contained"
                      sx={{
                        bgcolor: customColors?.primary || 'primary.main',
                        color: customColors?.primary ? getContrastText(customColors.primary) : 'white',
                        '&:hover': {
                          bgcolor: customColors?.primary || 'primary.main',
                          opacity: 0.9
                        }
                      }}
                    >
                      Adicionar
                    </Button>
                  </DialogActions>
                </Dialog>

                {/* Add Playlist Dialog */}
                <Dialog open={openPlaylistDialog} onClose={() => setOpenPlaylistDialog(false)}>
                  <DialogTitle>Adicionar Playlist do Spotify</DialogTitle>
                  <DialogContent>
                    <Stack spacing={2} sx={{ mt: 2, minWidth: 300 }}>
                      <TextField
                        label="URL da Playlist"
                        value={newPlaylistUrl}
                        onChange={(e) => setNewPlaylistUrl(e.target.value)}
                        fullWidth
                        helperText="Ex: https://open.spotify.com/playlist/..."
                      />
                      <TextField
                        label="Título da Playlist"
                        value={newPlaylistTitle}
                        onChange={(e) => setNewPlaylistTitle(e.target.value)}
                        fullWidth
                        placeholder="Digite um título para a playlist..."
                      />
                      <TextField
                        label="Anotações (opcional)"
                        value={playlistNote}
                        onChange={(e) => setPlaylistNote(e.target.value)}
                        fullWidth
                        multiline
                        rows={3}
                        placeholder="Adicione suas anotações sobre esta playlist..."
                      />
                    </Stack>
                  </DialogContent>
                  <DialogActions>
                    <Button onClick={() => setOpenPlaylistDialog(false)}>Cancelar</Button>
                    <Button 
                      onClick={handleAddPlaylist}
                      variant="contained"
                      sx={{
                        bgcolor: customColors?.primary || 'primary.main',
                        color: customColors?.primary ? getContrastText(customColors.primary) : 'white',
                        '&:hover': {
                          bgcolor: customColors?.primary || 'primary.main',
                          opacity: 0.9
                        }
                      }}
                    >
                      Adicionar
                    </Button>
                  </DialogActions>
                </Dialog>

                {/* Help Dialogs */}
                <Dialog open={openPomodoroHelp} onClose={() => setOpenPomodoroHelp(false)}>
                  <DialogTitle>Como funciona o Timer Pomodoro?</DialogTitle>
                  <DialogContent>
                    <Typography paragraph>
                      A Técnica <b>Pomodoro</b> é um método de gerenciamento de tempo que ajuda você a manter o foco e a produtividade. Funciona assim:
                    </Typography>
                    <Typography component="div" sx={{ mb: 2 }}>
                      <ul>
                        <li>Trabalhe focado por 25 minutos (um "pomodoro")</li>
                        <li>Faça uma pausa curta de 5 minutos</li>
                        <li>Após 4 pomodoros, faça uma pausa longa de 15 minutos</li>
                        <li>Repita o ciclo</li>
                      </ul>
                    </Typography>
                    <Typography paragraph>
                      Benefícios:
                    </Typography>
                    <Typography component="div">
                      <ul>
                        <li>Melhora o foco e a concentração</li>
                        <li>Reduz a fadiga mental</li>
                        <li>Aumenta a produtividade</li>
                        <li>Ajuda a evitar procrastinação</li>
                        <li>Mantém sua mente fresca com pausas regulares</li>
                      </ul>
                    </Typography>
                    <Typography sx={{ mt: 2 }}>
                      Você pode personalizar os tempos de trabalho e pausa nas configurações do timer para se adequar melhor ao seu ritmo!
                    </Typography>
                  </DialogContent>
                  <DialogActions>
                    <Button onClick={() => setOpenPomodoroHelp(false)}>Fechar</Button>
                  </DialogActions>
                </Dialog>

                <Dialog open={openVideoHelp} onClose={() => setOpenVideoHelp(false)}>
                  <DialogTitle>Como usar os Vídeos para Foco?</DialogTitle>
                  <DialogContent>
                    <Typography paragraph>
                      Os vídeos para foco são uma ferramenta poderosa para melhorar sua concentração durante as sessões de trabalho:
                    </Typography>
                    <Typography component="div" sx={{ mb: 2 }}>
                      <ul>
                        <li>Adicione vídeos do YouTube com sons ambientes ou músicas relaxantes</li>
                        <li>O áudio será reproduzido em segundo plano enquanto você usa a Habitop</li>
                        <li>Você pode alternar entre diferentes vídeos conforme sua preferência</li>
                        <li>Organize seus vídeos favoritos com notas personalizadas</li>
                      </ul>
                    </Typography>
                    <Typography paragraph>
                      Sugestões de conteúdo:
                    </Typography>
                    <Typography component="div">
                      <ul>
                        <li>Música lo-fi</li>
                        <li>Sons da natureza</li>
                        <li>Ruído branco</li>
                        <li>Música clássica</li>
                        <li>Sons de café/biblioteca</li>
                        <li>Discursos motivacionais</li>
                      </ul>
                    </Typography>
                    <Typography sx={{ mt: 2 }}>
                      Experimente diferentes tipos de sons para descobrir o que melhor ajuda em sua concentração.
                    </Typography>
                  </DialogContent>
                  <DialogActions>
                    <Button onClick={() => setOpenVideoHelp(false)}>Fechar</Button>
                  </DialogActions>
                </Dialog>

                {/* Edit Video Dialog */}
                <Dialog open={openEditVideoDialog} onClose={() => setOpenEditVideoDialog(false)}>
                  <DialogTitle>Editar Vídeo</DialogTitle>
                  <DialogContent>
                    <Stack spacing={2} sx={{ mt: 2, minWidth: 300 }}>
                      <Typography variant="subtitle1">
                        {editingVideo?.title}
                      </Typography>
                      <TextField
                        select
                        label="Categoria"
                        value={editingVideo?.category || ''}
                        onChange={(e) => setEditingVideo(prev => ({ ...prev, category: e.target.value }))}
                        fullWidth
                      >
                        {videoCategories.map((category) => (
                          <MenuItem key={category} value={category}>
                            {category}
                          </MenuItem>
                        ))}
                      </TextField>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <TextField
                          label="Nova categoria"
                          value={newCategory}
                          onChange={(e) => setNewCategory(e.target.value)}
                          size="small"
                          fullWidth
                        />
                        <Button
                          variant="outlined"
                          onClick={handleAddCategory}
                          sx={{ whiteSpace: 'nowrap' }}
                        >
                          Adicionar
                        </Button>
                      </Box>
                      <TextField
                        label="Anotações"
                        value={editingVideo?.note || ''}
                        onChange={(e) => setEditingVideo(prev => ({ ...prev, note: e.target.value }))}
                        fullWidth
                        multiline
                        rows={3}
                        placeholder="Adicione suas anotações sobre este vídeo..."
                      />
          </Stack>
        </DialogContent>
        <DialogActions>
                    <Button onClick={() => setOpenEditVideoDialog(false)}>Cancelar</Button>
                    <Button 
                      onClick={handleEditVideo}
                      variant="contained"
                      sx={{
                        bgcolor: customColors?.primary || 'primary.main',
                        color: customColors?.primary ? getContrastText(customColors.primary) : 'white',
                        '&:hover': {
                          bgcolor: customColors?.primary || 'primary.main',
                          opacity: 0.9
                        }
                      }}
                    >
                      Salvar
                    </Button>
        </DialogActions>
      </Dialog>

      <PlanComparisonDialog 
        open={showPlanDialog} 
        onClose={() => setShowPlanDialog(false)}
        customColors={customColors}
      />
    </PageLayout>
            </>
  );
} 